import { alpha, createTheme } from "@mui/material/styles";
import { DEFAULT_BRAND } from "../../config/brand-colors";
import { DARK_SURFACE, LIGHT_SURFACE } from "../../theme/surfaceTokens";

const getPrimaryDark = (mode) =>
  typeof mode.darkTint === "string" ? mode.darkTint : mode.hover;

export const commonThemes = createTheme({
  inputLabel: {
    fontFamily: "Inter",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "24px",
    letterSpacing: "0.15px",
  },
  pageTitle: {
    fontFamily: "Inter",
    fontSize: "28px",
    fontStyle: "normal",
    fontWeight: "300",
    lineHeight: "24px",
    letterSpacing: "0.15px",
  },
  popupTitle: {
    fontFamily: "Inter",
    fontSize: "24px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "36px",
    letterSpacing: "-0.264px",
  },
  footer: {
    text: {
      fontFamily: "Inter",
      fontSize: "13px",
      fontWeight: 400,
      lineHeight: "100%",
      letterSpacing: "0.15px",
    },
  },
});

export const createLightTheme = (brand = DEFAULT_BRAND) => {
  const c = brand.light;

  return createTheme({
  ...commonThemes,
  palette: {
    mode: "light",
    primary: {
      main: c.main,
      light: c.chartLight,
      dark: getPrimaryDark(c),
      contrastText: c.contrastText ?? "#fff",
    },
    secondary: {
      main: c.chartMuted,
      light: alpha(c.chartLight, 0.8),
      dark: alpha(c.chartMuted, 0.3),
    },
    text: {
      primary: LIGHT_SURFACE.textPrimary,
      secondary: LIGHT_SURFACE.textSecondary,
      tertiary: alpha(LIGHT_SURFACE.textPrimary, 0.6),
      disabled: alpha(LIGHT_SURFACE.textPrimary, 0.38),
      menuItemText: LIGHT_SURFACE.textPrimary,
    },
    background: {
      default: LIGHT_SURFACE.bgDefault,
      paper: LIGHT_SURFACE.bgPaper,
    },
    action: {
      hover: alpha("#000", 0.04),
      active: alpha(c.main, c.actionActiveAlpha),
    },
    divider: LIGHT_SURFACE.divider,
    blackWhite: {
      main: "#000",
    },
    borders: {
      main: "none",
    },
    footer: {
      text: LIGHT_SURFACE.textSecondary,
      background: LIGHT_SURFACE.footerBg,
    },
  },
  components: {
    MuiFormLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.secondary,
        }),
      },
    },
    MuiContainer: {
      defaultProps: {
        maxWidth: 1664,
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          transition: "color 0.1s",
          a: {
            transition: "color 0.1s",
          },
          "&:hover": {
            backgroundColor: "transparent",
            color: theme.palette.primary.main,
            a: {
              color: `${theme.palette.primary.main} !important`,
            },
          },
        }),
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
        }),
        icon: {
          fill: "rgba(31, 29, 29, 0.8)",
        },
      },
    },
    MuiDatePicker: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
        }),
      },
      defaultProps: {
        size: "small",
        slotProps: {
          openPickerButton: {
            sx: {
              color: "rgba(255, 255, 255, 0.56)",
            },
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        InputLabelProps: { shrink: true },
        size: "small",
      },
    },
    MuiTypography: {
      variants: [
        {
          props: { variant: "sectionTitle" },
          style: ({ theme }) => ({
            color: theme.palette.text.secondary,
            fontSize: "16px",
            fontWeight: 600,
            lineHeight: "24px",
          }),
        },
        {
          props: { variant: "sectionLabel" },
          style: ({ theme }) => ({
            color: theme.palette.text.secondary,
            fontSize: "14px",
            fontWeight: 500,
          }),
        },
      ],
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          textTransform: "none",
          fontFamily: "Inter",
          fontWeight: 700,
        },
        containedPrimary: ({ theme }) => ({
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          "&:hover": {
            backgroundColor: theme.palette.primary.dark,
          },
          "&.Mui-disabled": {
            backgroundColor: alpha(theme.palette.primary.main, 0.38),
            color: alpha(theme.palette.primary.contrastText, 0.65),
          },
        }),
        contained: {
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
          },
        },
        outlined: ({ theme }) => ({
          borderWidth: "1px",
          "&:hover": {
            borderWidth: "1px",
            backgroundColor: alpha(theme.palette.primary.main, 0.04),
          },
        }),
      },
      variants: [
        {
          props: { variant: "contained", color: "primary" },
          style: ({ theme }) => ({
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
            },
            "&.Mui-disabled": {
              backgroundColor: alpha(theme.palette.primary.main, 0.38),
              color: alpha(theme.palette.primary.contrastText, 0.65),
            },
          }),
        },
        {
          props: { variant: "outlined", color: "primary" },
          style: ({ theme }) => ({
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
          }),
        },
        {
          props: { variant: "outlined", color: "secondary" },
          style: ({ theme }) => ({
            borderColor: theme.palette.secondary.main,
            color: theme.palette.secondary.main,
          }),
        },
        {
          props: { variant: "secondary" },
          style: ({ theme, ownerState }) => ({
            backgroundColor: alpha(theme.palette[ownerState.color].main, 0.08),
            color: theme.palette[ownerState.color].main,
            "&:hover": {
              backgroundColor: alpha(
                theme.palette[ownerState.color].main,
                0.04
              ),
            },
          }),
        },
      ],
      defaultProps: {
        disableElevation: true,
      },
    },
    MuiCheckbox: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.primary.main,
          "&.Mui-checked": {
            color: theme.palette.primary.main,
          },
        }),
      },
    },
    MuiRadio: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.primary.main,
          "&.Mui-checked": {
            color: theme.palette.primary.main,
          },
        }),
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        popupIndicator: {
          color: "rgba(0, 0, 0, 0.8)",
          "&:hover": {
            color: "rgba(0, 0, 0, 0.8)",
            "& .MuiSvgIcon-root": {
              fill: "rgba(0, 0, 0, 0.8)",
              stroke: "none !important",
            },
          },
          "& .MuiSvgIcon-root": {
            fill: "rgba(0, 0, 0, 0.8)",
            stroke: "none !important",
          },
        },
        popupIndicatorOpen: {
          color: "rgba(0, 0, 0, 0.8)",
          "&:hover": {
            color: "rgba(0, 0, 0, 0.8)",
            ".MuiSvgIcon-root": {
              stroke: "none !important",
            },
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          ...commonThemes.inputLabel,
          color: "rgba(0, 0, 0, 0.6)",
        },
      },
    },

    MuiTabs: {
      styleOverrides: {
        indicator: ({ theme }) => ({
          backgroundColor: theme.palette.primary.main,
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.secondary,
          "&.Mui-selected": {
            color: theme.palette.primary.main,
          },
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: alpha(theme.palette.text.primary, 0.08),
          color: theme.palette.text.primary,
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
        },
        notchedOutline: ({ theme }) => ({
          borderColor: alpha(theme.palette.text.primary, 0.23),
        }),
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
        }),
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
        }),
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          ".MuiSvgIcon-root": {
            fill: "rgba(31, 29, 29, 0.8)",
            stroke: "none",
            transition: "0.1s",
          },
          "&:hover": {
            backgroundColor: "transparent",
            ".MuiSvgIcon-root": {
              fill: theme.palette.primary.main,
            },
            ".MuiTypography-root, + .MuiTypography-root": {
              color: `${theme.palette.primary.main} !important`,
              transition: "color 0.1s",
            },
          },
          ".MuiTypography-root": {
            color: "#757575",
            transition: "color 0.1s",
          },
        }),
      },
    },
  },
  typography: {
    fontFamily: "Inter",
    fontSize: 14,
    h1: {
      fontSize: 24,
      fontWeight: 600,
    },
    h2: {
      fontSize: 20,
      fontWeight: 600,
    },
    h3: {
      fontSize: 16,
      fontWeight: 600,
    },
    body1: {
      fontSize: 14,
    },
    body2: {
      fontSize: 12,
    },
    button: {
      textTransform: "none",
    },
    linkColor: c.main,
  },
});
};

export const createDarkTheme = (brand = DEFAULT_BRAND) => {
  const d = brand.dark;

  return createTheme({
  ...commonThemes,
  palette: {
    mode: "dark",
    primary: {
      main: d.main,
      light: d.chartLight,
      dark: getPrimaryDark(d),
      contrastText: d.contrastText ?? "#fff",
    },
    secondary: {
      main: d.chartMuted,
      light: alpha(d.chartLight, 0.8),
      dark: alpha(d.chartMuted, 0.3),
    },
    text: {
      primary: DARK_SURFACE.textPrimary,
      secondary: DARK_SURFACE.textSecondary,
      tertiary: alpha(DARK_SURFACE.textPrimary, 0.6),
      disabled: alpha(DARK_SURFACE.textPrimary, 0.38),
      menuItemText: DARK_SURFACE.textSecondary,
    },
    background: {
      default: DARK_SURFACE.bgDefault,
      paper: DARK_SURFACE.bgPaper,
    },
    action: {
      hover: alpha("#000", 0.04),
      active: alpha(d.actionActiveBase ?? d.main, d.actionActiveAlpha),
    },
    divider: DARK_SURFACE.divider,
    blackWhite: {
      main: "#FFF",
    },
    borders: {
      main: `1px solid ${DARK_SURFACE.divider}`,
    },
    footer: {
      text: DARK_SURFACE.footerText,
      background: DARK_SURFACE.footerBg,
    },
  },
  components: {
    MuiFormLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.secondary,
        }),
      },
    },
    MuiContainer: {
      defaultProps: {
        maxWidth: 1664,
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: DARK_SURFACE.bgPaper,
          backgroundImage: "none",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "unset",
        },
      },
    },

    MuiPopover: {
      styleOverrides: {
        paper: {
          backgroundImage: "none",
          backgroundColor: DARK_SURFACE.bgPaper,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.primary,
          a: {
            transition: "color 0.1s",
          },
          "&:hover": {
            backgroundColor: "transparent",
            color: theme.palette.primary.main,
            a: {
              color: `${theme.palette.primary.main} !important`,
            },
          },
        }),
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          ".MuiDataGrid-pinnedColumns, .MuiDataGrid-pinnedColumnHeaders": {
            backgroundImage: "none !important",
          },
        },
      },
    },
    MuiDatePicker: {
      defaultProps: {
        size: "small",
        slotProps: {
          openPickerButton: {
            sx: (theme) => ({
              color: "rgba(255, 255, 255, 0.56)",
              "&:hover": {
                color: theme.palette.primary.main,
              },
            }),
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        InputLabelProps: { shrink: true },
        size: "small",
      },
    },
    MuiTypography: {
      variants: [
        {
          props: { variant: "sectionTitle" },
          style: ({ theme }) => ({
            color: theme.palette.text.secondary,
            fontSize: "16px",
            fontWeight: 600,
            lineHeight: "24px",
          }),
        },
        {
          props: { variant: "sectionLabel" },
          style: ({ theme }) => ({
            color: theme.palette.text.secondary,
            fontSize: "14px",
            fontWeight: 500,
          }),
        },
      ],
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          textTransform: "none",
          fontFamily: "Inter",
          fontWeight: 700,
        },
        containedPrimary: ({ theme }) => ({
          backgroundColor: theme.palette.primary.main,
          color: `${theme.palette.primary.contrastText} !important`,
          "&:hover": {
            backgroundColor: theme.palette.primary.dark,
          },
          "&.Mui-disabled": {
            backgroundColor: alpha(theme.palette.primary.main, 0.38),
            color: `${alpha(theme.palette.primary.contrastText, 0.65)} !important`,
          },
        }),
        contained: {
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
          },
        },
        outlined: ({ theme }) => ({
          borderWidth: "1px",
          "&:hover": {
            borderWidth: "1px",
            backgroundColor: alpha(theme.palette.primary.main, 0.04),
          },
        }),
      },
      variants: [
        {
          props: { variant: "contained", color: "primary" },
          style: ({ theme }) => ({
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
            },
            "&.Mui-disabled": {
              backgroundColor: alpha(theme.palette.primary.main, 0.38),
              color: alpha(theme.palette.primary.contrastText, 0.65),
            },
          }),
        },
        {
          props: { variant: "outlined", color: "primary" },
          style: ({ theme }) => ({
            borderColor: alpha(theme.palette.primary.main, 0.8),
            color: theme.palette.primary.main,
          }),
        },
        {
          props: { variant: "outlined", color: "secondary" },
          style: ({ theme }) => ({
            borderColor: alpha(theme.palette.secondary.main, 0.8),
            color: theme.palette.secondary.main,
          }),
        },
        {
          props: { variant: "secondary" },
          style: ({ theme, ownerState }) => ({
            backgroundColor: alpha(theme.palette[ownerState.color].main, 0.08),
            color: theme.palette[ownerState.color].main,
            "&:hover": {
              backgroundColor: alpha(
                theme.palette[ownerState.color].main,
                0.04
              ),
            },
          }),
        },
      ],
      defaultProps: {
        disableElevation: true,
      },
    },
    MuiCheckbox: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.primary.main,
          "&.Mui-checked": {
            color: theme.palette.primary.main,
          },
        }),
      },
    },
    MuiRadio: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.primary.main,
          "&.Mui-checked": {
            color: theme.palette.primary.main,
          },
        }),
      },
    },
    MuiSelect: {
      styleOverrides: {
        icon: {
          color: "rgba(255, 255, 255, 0.56)",
        },
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        popupIndicator: {
          color: "rgba(255, 255, 255, 0.56)",
          "&:hover": {
            color: "rgba(255, 255, 255, 0.56)",
            "& .MuiSvgIcon-root": {
              fill: "rgba(255, 255, 255, 0.56)",
              stroke: "none !important",
            },
          },
          "& .MuiSvgIcon-root": {
            fill: "rgba(255, 255, 255, 0.56)",
            stroke: "none !important",
          },
        },
        popupIndicatorOpen: {
          color: "rgba(255, 255, 255, 0.56)",
          "&:hover": {
            color: "rgba(255, 255, 255, 0.56)",
            ".MuiSvgIcon-root": {
              stroke: "none !important",
            },
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          ...commonThemes.inputLabel,
          color: "#FFF",
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          ".MuiSvgIcon-root": {
            fill: "rgba(255, 255, 255, 0.56)",
            stroke: "none",
            transition: "0.1s",
          },
          "&:hover": {
            backgroundColor: "transparent",
            ".MuiSvgIcon-root": {
              fill: theme.palette.primary.main,
            },
            ".MuiTypography-root, + .MuiTypography-root": {
              color: `${theme.palette.primary.main} !important`,
              transition: "color 0.1s",
            },
          },
          ".MuiTypography-root": {
            // color: "#757575",
            transition: "color 0.1s",
          },

          svg: {
            stroke: "rgba(255, 255, 255, 0.87)",
            path: {
              transition: "stroke 0.1s",
            },
          },
          ".MuiTypography-root": {
            color: "rgba(255, 255, 255, 0.87)",
            transition: "color 0.1s",
          },
        }),
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          backgroundColor: "none",
        },
        indicator: ({ theme }) => ({
          backgroundColor: theme.palette.primary.main,
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.secondary,
          "&.Mui-selected": {
            color: theme.palette.primary.main,
          },
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: alpha(theme.palette.secondary.main, 0.16),
          color: theme.palette.text.primary,
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
        },
        notchedOutline: ({ theme }) => ({
          borderColor: alpha(theme.palette.text.primary, 0.23),
        }),
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
        }),
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.background.default,
        }),
      },
    },
  },
  typography: {
    fontFamily: "Inter",
    fontSize: 14,
    h1: {
      fontSize: 24,
      fontWeight: 600,
    },
    h2: {
      fontSize: 20,
      fontWeight: 600,
    },
    h3: {
      fontSize: 16,
      fontWeight: 600,
    },
    body1: {
      fontSize: 14,
    },
    body2: {
      fontSize: 12,
    },
    button: {
      textTransform: "none",
    },
    linkColor: d.main,
  },
});
};

/** @deprecated Use getAppLightTheme() / getAppTheme() from config/resolve-app-brand */
export const lightTheme = createLightTheme();
/** @deprecated Use getAppDarkTheme() / getAppTheme() from config/resolve-app-brand */
export const darkTheme = createDarkTheme();
